import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import type { NostrEvent } from '@nostrify/nostrify';
import { Check, Loader2, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import {
  useCreateHighlight,
  useDeleteHighlight,
  useHighlights,
} from '@/hooks/useHighlights';
import { applyHighlightMark, HIGHLIGHT_MARK_ATTR, unwrapHighlightMarks } from '@/lib/highlightDom';
import type { HighlightTarget } from '@/lib/nip84';
import { cn } from '@/lib/utils';

interface HighlightLayerProps {
  event: NostrEvent;
  /** `kind:pubkey:d` coordinate, required when `event.kind` is addressable (30000-39999). */
  addressPointer?: string;
  /** Whether highlight mode is currently on — see `HighlightToggle`. */
  active: boolean;
  children: ReactNode;
  className?: string;
}

const BUBBLE_MAX_LENGTH = 600;

/**
 * Wraps a rendered wiki article/section body, and while highlight mode
 * is active:
 *
 *  - Renders every public NIP-84 highlight (`kind:9802`) on this exact
 *    version as a `<mark>` over the matching text, found by plain text
 *    search (see `@/lib/highlightDom` — NIP-84 has no position anchors,
 *    only the highlighted string itself).
 *  - Lets a logged-in reader select any passage of the rendered text and
 *    publish it as their own highlight via a small floating "Highlight"
 *    button that appears next to the selection.
 *  - Lets a reader click an existing highlight to see who made it, and
 *    remove their own highlights.
 *
 * The DOM mutation this performs (wrapping/unwrapping `<mark>` elements)
 * is intentionally kept out of React's render tree — see the doc
 * comment on `@/lib/highlightDom` for why a text-search-based approach
 * is used instead of tracking offsets.
 */
export function HighlightLayer({ event, addressPointer, active, children, className }: HighlightLayerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const target: HighlightTarget = { id: event.id, pubkey: event.pubkey, kind: event.kind, addressPointer };

  const { user } = useCurrentUser();
  const { data } = useHighlights(target);
  const highlights = data?.highlights ?? [];

  const { mutate: createHighlight, isPending: isCreating } = useCreateHighlight(target);
  const { mutate: deleteHighlight, isPending: isDeleting } = useDeleteHighlight(target);

  const [pendingSelection, setPendingSelection] = useState<{
    text: string;
    context?: string;
    top: number;
    left: number;
  } | null>(null);
  const [selectedHighlight, setSelectedHighlight] = useState<{
    highlight: NostrEvent;
    top: number;
    left: number;
  } | null>(null);

  // Re-render every active highlight as a <mark> whenever the set of
  // highlights (or highlight-mode itself) changes. Always unwrap first —
  // this effect can re-run after the underlying content hasn't actually
  // changed, and marks must never be double-applied.
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    unwrapHighlightMarks(container);
    setSelectedHighlight(null);

    if (!active) return;

    for (const highlight of highlights) {
      applyHighlightMark(container, highlight.content, (mark) => {
        mark.setAttribute(HIGHLIGHT_MARK_ATTR, '');
        mark.dataset.highlightId = highlight.id;
        mark.className = cn(
          'cursor-pointer rounded-sm bg-yellow-200/70 px-0.5 decoration-yellow-500 transition-colors hover:bg-yellow-300/70 dark:bg-yellow-500/30 dark:hover:bg-yellow-500/40',
          highlight.pubkey === user?.pubkey && 'ring-1 ring-yellow-500/50',
        );
      });
    }

    return () => {
      // Clean up when this effect re-runs or the layer unmounts, so a
      // stale mark set never lingers if `active` flips off between runs.
      if (container) unwrapHighlightMarks(container);
    };
    // `children` isn't referenced directly, but the rendered body it
    // produces is exactly what these marks are applied over — re-run
    // whenever it (or the format it's rendered in) could have changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, highlights, user?.pubkey, children]);

  // Detect a text selection made by the reader, while highlight mode is
  // on, anywhere inside this container — and show a small "Highlight"
  // button near it. Uses `selectionchange` (not just mouseup) so
  // keyboard-driven selection (Shift+Arrow) works too.
  useLayoutEffect(() => {
    if (!active || !user) {
      setPendingSelection(null);
      return;
    }

    const handleSelectionChange = () => {
      const container = containerRef.current;
      const selection = window.getSelection();
      if (!container || !selection || selection.isCollapsed || selection.rangeCount === 0) {
        setPendingSelection(null);
        return;
      }

      const range = selection.getRangeAt(0);
      if (!container.contains(range.commonAncestorContainer)) {
        setPendingSelection(null);
        return;
      }

      const text = selection.toString().trim();
      if (!text) {
        setPendingSelection(null);
        return;
      }

      const rect = range.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      // Best-effort surrounding-paragraph context, per NIP-84's optional
      // `context` tag — silently omitted if it can't be resolved.
      let contextEl: Node | null = range.commonAncestorContainer;
      if (contextEl.nodeType === Node.TEXT_NODE) contextEl = contextEl.parentElement;
      const blockAncestor = (contextEl as Element | null)?.closest?.('p, li, blockquote, td, th, h1, h2, h3, h4, h5, h6');
      const context = blockAncestor?.textContent?.trim().slice(0, BUBBLE_MAX_LENGTH);

      setPendingSelection({
        text: text.slice(0, BUBBLE_MAX_LENGTH),
        context: context && context !== text ? context : undefined,
        top: rect.bottom - containerRect.top + 8,
        left: Math.max(0, rect.left - containerRect.left),
      });
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, [active, user]);

  const handleMarkClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!active) return;
    const mark = (e.target as HTMLElement).closest<HTMLElement>(`mark[${HIGHLIGHT_MARK_ATTR}]`);
    if (!mark || !mark.dataset.highlightId) return;
    const highlight = highlights.find((h) => h.id === mark.dataset.highlightId);
    if (!highlight) return;

    const container = containerRef.current;
    if (!container) return;
    const rect = mark.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    setPendingSelection(null);
    window.getSelection()?.removeAllRanges();
    setSelectedHighlight({
      highlight,
      top: rect.bottom - containerRect.top + 8,
      left: Math.max(0, rect.left - containerRect.left),
    });
  };

  const confirmHighlight = () => {
    if (!pendingSelection) return;
    createHighlight(
      { content: pendingSelection.text, context: pendingSelection.context },
      {
        onSuccess: () => {
          setPendingSelection(null);
          window.getSelection()?.removeAllRanges();
        },
      },
    );
  };

  return (
    <div ref={containerRef} className={cn('relative', className)} onClick={handleMarkClick}>
      {children}

      {active && pendingSelection && (
        <div
          className="absolute z-20 flex items-center gap-1 rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
          style={{ top: pendingSelection.top, left: pendingSelection.left }}
        >
          <Button type="button" size="sm" className="h-7 gap-1.5 px-2 text-xs" onClick={confirmHighlight} disabled={isCreating}>
            {isCreating ? <Loader2 className="size-3.5 animate-spin" /> : <Check className="size-3.5" />}
            Highlight
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-7"
            onClick={() => {
              setPendingSelection(null);
              window.getSelection()?.removeAllRanges();
            }}
            aria-label="Cancel"
          >
            <X className="size-3.5" />
          </Button>
        </div>
      )}

      {active && selectedHighlight && (
        <HighlightPopover
          highlight={selectedHighlight.highlight}
          top={selectedHighlight.top}
          left={selectedHighlight.left}
          isOwn={selectedHighlight.highlight.pubkey === user?.pubkey}
          isDeleting={isDeleting}
          onDelete={() => {
            deleteHighlight(selectedHighlight.highlight);
            setSelectedHighlight(null);
          }}
          onClose={() => setSelectedHighlight(null)}
        />
      )}
    </div>
  );
}

function HighlightPopover({
  highlight,
  top,
  left,
  isOwn,
  isDeleting,
  onDelete,
  onClose,
}: {
  highlight: NostrEvent;
  top: number;
  left: number;
  isOwn: boolean;
  isDeleting: boolean;
  onDelete: () => void;
  onClose: () => void;
}) {
  const author = useAuthor(highlight.pubkey);
  const displayName = author.data?.metadata?.name ?? `${highlight.pubkey.slice(0, 8)}…`;

  return (
    <div
      className="absolute z-20 flex max-w-xs items-center gap-2 rounded-md border bg-popover p-2 text-popover-foreground shadow-md"
      style={{ top, left }}
    >
      <p className="text-xs text-muted-foreground">
        Highlighted by <span className="font-medium text-foreground">{displayName}</span>
      </p>
      {isOwn && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-6 shrink-0 text-destructive hover:text-destructive"
          onClick={onDelete}
          disabled={isDeleting}
          aria-label="Remove highlight"
        >
          {isDeleting ? <Loader2 className="size-3.5 animate-spin" /> : <Trash2 className="size-3.5" />}
        </Button>
      )}
      <Button type="button" variant="ghost" size="icon" className="size-6 shrink-0" onClick={onClose} aria-label="Close">
        <X className="size-3.5" />
      </Button>
    </div>
  );
}
